import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Users, Trophy, Timer, Gift, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import RegistrationForm from '../components/RegistrationForm';
import { useEventConfig } from '../hooks/useEventConfig';
import { AuthContext } from '../App';

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { config } = useEventConfig();
  const levels = config.categories;
  const [eventTitle, eventVersion] = (() => {
    const match = /^(.*?)([\d.]+)$/.exec((config.event_name || '').trim());
    if (match) return [match[1].trim(), match[2]];
    return [config.event_name, ''];
  })();

  const fadeUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const scrollToForm = () => {
    document.getElementById('inscricao-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      {/* Floating Ranking button for logged-in users */}
      {user && (
        <button
          data-testid="floating-ranking-btn"
          onClick={() => navigate('/ranking')}
          className="fixed top-5 right-5 z-50 flex items-center gap-2 bg-primary/90 text-black px-4 py-2.5 rounded-full font-mono text-xs uppercase tracking-wider shadow-lg hover:bg-primary transition-all font-bold"
        >
          <Trophy className="w-4 h-4" />
          Ver Ranking
        </button>
      )}

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1758979937001-d5ccdbd8a231?q=85&w=1920&auto=format&fit=crop)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'brightness(0.3)'
          }}
        />
        <div className="absolute inset-0 hero-glow z-0" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="mb-8"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_71e90c7e-0627-431e-aeed-455d200cb65d/artifacts/oqvzxieq_R77%20logo.PNG"
              alt="R77 Logo"
              className="h-24 md:h-32 mx-auto neon-glow"
            />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-unbounded font-black text-6xl md:text-8xl tracking-tighter uppercase leading-[0.8] mb-8"
          >
            <span className="text-white" data-testid="hero-event-title">{eventTitle || 'R77 CHALLENGE'}</span>
            {eventVersion && (
              <>
                <br />
                <span className="text-primary" data-testid="hero-event-version">{eventVersion}</span>
              </>
            )}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="font-body text-lg md:text-xl text-muted-foreground mb-12 max-w-2xl mx-auto leading-relaxed"
            data-testid="hero-tagline"
          >
            {config.tagline}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <Button
              data-testid="hero-cta-button"
              onClick={scrollToForm}
              className="bg-primary text-black font-unbounded font-bold uppercase tracking-wider hover:bg-primary/90 hover:scale-105 transition-all duration-200 px-12 py-8 text-lg skew-btn"
            >
              <span>Quero me inscrever agora</span>
            </Button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1 }}
            className="mt-16 flex flex-wrap justify-center gap-8 text-sm font-mono uppercase tracking-wider text-muted-foreground"
          >
            <div className="flex items-center gap-2">
              <Timer className="w-4 h-4 text-primary" />
              <span data-testid="hero-duration">{config.duration_text}</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-primary" />
              <span>Em dupla</span>
            </div>
            <div className="flex items-center gap-2">
              <Trophy className="w-4 h-4 text-primary" />
              <span>Troféu para as duplas campeãs</span>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity, delay: 1.5 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-muted-foreground"
        >
          <div className="w-6 h-10 border-2 border-muted-foreground rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-primary rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* Como Funciona */}
      <section className="py-24 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.h2
            {...fadeUp}
            className="font-unbounded font-bold text-4xl md:text-5xl tracking-tight uppercase text-center mb-8"
          >
            Como <span className="text-primary">Funciona</span>
          </motion.h2>

          <motion.div
            {...fadeUp}
            className="max-w-3xl mx-auto mb-16 text-center"
          >
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
              Você e sua <span className="text-primary font-bold">dupla</span> têm
              <span className="text-primary font-bold"> {config.duration_text}</span> para acumular o máximo de quilômetros e conquistar o topo da sua categoria.
              A dupla com melhor desempenho leva <span className="text-white font-semibold">troféu + brindes</span>.
            </p>
          </motion.div>

          {/* Categorias */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {levels.map((level, index) => (
              <motion.div
                key={index}
                {...fadeUp}
                transition={{ delay: index * 0.15 }}
                className="group relative overflow-hidden bg-card border border-white/10 p-8 hover:border-primary/50 transition-colors duration-300"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 rounded-full blur-3xl" />
                <h3 className="font-unbounded font-bold text-2xl mb-4 relative">{level}</h3>
                <p className="text-muted-foreground font-mono text-sm uppercase tracking-wider relative">
                  Categoria disponível
                </p>
              </motion.div>
            ))}
          </div>

          {/* Info cards */}
          <motion.div {...fadeUp} className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-card border border-white/10 p-6">
              <Calendar className="w-8 h-8 text-primary mb-3" />
              <h4 className="font-unbounded font-bold text-lg mb-2">Duração</h4>
              <p className="text-muted-foreground text-sm">{config.duration_text} de desafio</p>
            </div>
            <div className="bg-card border border-white/10 p-6">
              <Users className="w-8 h-8 text-primary mb-3" />
              <h4 className="font-unbounded font-bold text-lg mb-2">Em Dupla</h4>
              <p className="text-muted-foreground text-sm">Você e um parceiro na mesma categoria</p>
            </div>
            <div className="bg-card border border-white/10 p-6">
              <Trophy className="w-8 h-8 text-primary mb-3" />
              <h4 className="font-unbounded font-bold text-lg mb-2">Premiação</h4>
              <p className="text-muted-foreground text-sm">Troféu para as duplas campeãs de cada categoria</p>
            </div>
            <div className="bg-card border border-white/10 p-6">
              <Gift className="w-8 h-8 text-primary mb-3" />
              <h4 className="font-unbounded font-bold text-lg mb-2">Brindes & Atividades</h4>
              <p className="text-muted-foreground text-sm">Atividades especiais e brindes ao longo do desafio</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 px-4 md:px-8 bg-secondary/30">
        <div className="max-w-3xl mx-auto">
          <motion.h2
            {...fadeUp}
            className="font-unbounded font-bold text-4xl md:text-5xl tracking-tight uppercase text-center mb-16"
          >
            Valor de <span className="text-primary">Inscrição</span>
          </motion.h2>

          <motion.div
            {...fadeUp}
            className="relative bg-card border-2 border-primary p-10 flex flex-col gap-6 hover:shadow-[0_0_40px_rgba(197,255,0,0.2)] transition-all duration-300 text-center"
          >
            <div>
              <h3 className="font-unbounded font-bold text-3xl mb-2">Inscrição Individual</h3>
              <p className="text-muted-foreground text-sm">Preço por participante</p>
            </div>
            <div className="flex items-baseline justify-center gap-2" data-testid="pricing-value">
              <span className="text-6xl md:text-7xl font-bold text-primary">R$ {Math.trunc(config.price)}</span>
              <span className="text-muted-foreground text-xl">
                ,{String(Math.round((config.price - Math.trunc(config.price)) * 100)).padStart(2, '0')}
              </span>
            </div>
            <ul className="space-y-3 max-w-md mx-auto text-left">
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <span className="text-muted-foreground">Participação no desafio de {config.duration_text}</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <span className="text-muted-foreground">Atividades especiais ao longo do evento</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <span className="text-muted-foreground">Brindes exclusivos R77</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <span className="text-muted-foreground">Troféu para as duplas campeãs</span>
              </li>
            </ul>
            <Button
              data-testid="pricing-cta-button"
              onClick={scrollToForm}
              className="w-full max-w-sm mx-auto bg-primary text-black font-unbounded font-bold uppercase tracking-wider hover:bg-primary/90 hover:scale-105 transition-all duration-200 skew-btn"
            >
              <span>Fazer minha inscrição</span>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Registration Section */}
      <section id="inscricao-section" className="py-24 px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div {...fadeUp} className="text-center mb-12">
            <h2 className="font-unbounded font-bold text-4xl md:text-5xl tracking-tight uppercase mb-4">
              <span className="text-white">Faça sua</span>{' '}
              <span className="text-primary">Inscrição</span>
            </h2>
            <p className="text-muted-foreground text-lg">
              Preencha os dados abaixo e garanta sua vaga
            </p>
          </motion.div>
          <RegistrationForm />
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-muted-foreground font-mono text-sm uppercase tracking-wider">
            {config.event_name} © {config.event_year} | R77 Assessoria Esportiva
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
